import { isEmpty, isEqual } from 'bold-ui'
import { resolveName } from 'components/form'
import { RefObject, useCallback, useState } from 'react'
import { useForm } from 'react-final-form'
import { useHistory, useRouteMatch } from 'react-router'
import { metaPath } from 'util/metaPath'

import {
  hasAnyPerguntaData,
  initializeFirstPerguntaStatus,
  pruneAplicacao,
  setPerguntaAtivaToNextStatus,
} from '../consulta-seguimento-aplicacao/utils-consultaSeguimentoAplicacao'
import { ConsultaSeguimentoModalTab } from '../enums-consultaSeguimento'
import { CONSULTA_SEGUIMENTO_URL_MODAL, ConsultaSeguimentoFieldModel } from '../model-consultaSeguimento'

interface UseConsultaSeguimentoModalProps {
  aplicarButtonRef: RefObject<HTMLButtonElement>
  valueInCache: ConsultaSeguimentoFieldModel | undefined
  isConsultaSeguimentoDesatualizada: boolean
  onConfirmCloseModal: (handleConfirm: () => void) => void
}

const path = metaPath<ConsultaSeguimentoFieldModel>()

export function useConsultaSeguimentoModal(props: Readonly<UseConsultaSeguimentoModalProps>) {
  const { aplicarButtonRef, valueInCache, isConsultaSeguimentoDesatualizada, onConfirmCloseModal } = props

  const match = useRouteMatch()
  const history = useHistory()
  const { reset, getFieldState } = useForm<ConsultaSeguimentoFieldModel>()

  const hasValueInCache = !isEmpty(valueInCache)

  const [currentTab, setCurrentTab] = useState<ConsultaSeguimentoModalTab>(
    hasValueInCache && !isConsultaSeguimentoDesatualizada
      ? ConsultaSeguimentoModalTab.APLICACAO
      : ConsultaSeguimentoModalTab.SELECAO
  )

  const goBack = useCallback(() => {
    history.push(match.url.split(CONSULTA_SEGUIMENTO_URL_MODAL)[0])
    setTimeout(() => aplicarButtonRef?.current?.focus())
  }, [aplicarButtonRef, history, match.url])

  const getIsAplicacaoDiff = useCallback(() => {
    const inCacheAplicacao = valueInCache?.aplicacao
    const currentAplicacao = getFieldState(resolveName(path.aplicacao))?.value
    const updatedAplicacao = setPerguntaAtivaToNextStatus(currentAplicacao)
    const prunedAplicacao = pruneAplicacao(updatedAplicacao)
    return !isEqual(inCacheAplicacao, prunedAplicacao)
  }, [getFieldState, valueInCache?.aplicacao])

  const handleCloseModal = useCallback(() => {
    let showConfirmDialog = false

    const isAplicacao = currentTab === ConsultaSeguimentoModalTab.APLICACAO
    const isSelecao = currentTab === ConsultaSeguimentoModalTab.SELECAO

    if (isSelecao) {
      const isPerguntasSelecionadasModified = getFieldState(resolveName(path.perguntasSelecionadas))?.modified

      const hasAplicacaoChanges = hasValueInCache
        ? getIsAplicacaoDiff()
        : hasAnyPerguntaData(getFieldState(resolveName(path.aplicacao))?.value)

      showConfirmDialog = isPerguntasSelecionadasModified || hasAplicacaoChanges
    }

    if (isAplicacao) {
      if (hasValueInCache) {
        showConfirmDialog = getIsAplicacaoDiff()
      } else {
        showConfirmDialog = true
      }
    }

    if (showConfirmDialog) {
      onConfirmCloseModal(goBack)
    } else {
      goBack()
    }
  }, [currentTab, hasValueInCache, getFieldState, getIsAplicacaoDiff, onConfirmCloseModal, goBack])

  const handleGoToSelecao = useCallback(() => {
    setCurrentTab(ConsultaSeguimentoModalTab.SELECAO)
  }, [])

  const handleGoToAplicacao = useCallback(() => {
    setCurrentTab(ConsultaSeguimentoModalTab.APLICACAO)
  }, [])

  const handleGoBackToAplicacao = useCallback(() => {
    if (valueInCache) {
      reset({ ...valueInCache, aplicacao: initializeFirstPerguntaStatus(valueInCache.aplicacao) })
    } else {
      reset()
    }

    handleGoToAplicacao()
  }, [handleGoToAplicacao, reset, valueInCache])

  const handleGoToResultado = useCallback(() => {
    setCurrentTab(ConsultaSeguimentoModalTab.RESULTADO)
  }, [])

  return {
    currentTab,
    handleGoToSelecao,
    handleGoToAplicacao,
    handleGoBackToAplicacao,
    handleGoToResultado,
    handleCloseModal,
  }
}
